const API_BASE_URL = 'https://patrixglobal.com/backend/api';

export interface NavigationMenuItem {
  id: number;
  name: string;
  slug: string;
  href: string;
  flag: string;
  fallback: string;
  country_code: string;
  menu_position: 'main' | 'services' | 'footer';
  display_order: number;
  is_featured: boolean;
  title?: string;
  subtitle?: string;
  // Landing card customization
  card_gradient_from?: string;
  card_gradient_to?: string;
  card_icon?: string;
  card_features?: string[];
  card_description?: string;
}

export interface OrganizedMenuItems {
  main: NavigationMenuItem[];
  services: NavigationMenuItem[];
  footer: NavigationMenuItem[];
}

export interface MenuItemsResponse {
  success: boolean;
  items: NavigationMenuItem[] | OrganizedMenuItems;
  count: number;
}

/**
 * Fetch navigation menu items from the backend API
 * @param lang - Language code (en or ar)
 * @param menuPosition - Menu position filter: 'services', 'main', 'footer', or 'all'
 * @returns Promise with menu items
 */
export const fetchNavigationMenuItems = async (
  lang: string = 'en',
  menuPosition: 'services' | 'main' | 'footer' | 'all' = 'services'
): Promise<MenuItemsResponse> => {
  try {
    const url = `${API_BASE_URL}/navigation/menu-items.php?lang=${encodeURIComponent(lang)}&menu_position=${encodeURIComponent(menuPosition)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch navigation menu items');
    }

    if (data.success) {
      return data;
    } else {
      throw new Error('Failed to fetch navigation menu items');
    }
  } catch (error) {    throw error;
  }
};

/**
 * Fetch only service menu items (for company formation dropdown)
 * @param lang - Language code (en or ar)
 * @returns Promise with service menu items
 */
export const fetchServiceMenuItems = async (lang: string = 'en'): Promise<NavigationMenuItem[]> => {
  const response = await fetchNavigationMenuItems(lang, 'services');
  return response.items as NavigationMenuItem[];
};

/**
 * Fetch only main menu items
 * @param lang - Language code (en or ar)
 * @returns Promise with main menu items
 */
export const fetchMainMenuItems = async (lang: string = 'en'): Promise<NavigationMenuItem[]> => {
  const response = await fetchNavigationMenuItems(lang, 'main');
  return response.items as NavigationMenuItem[];
};

/**
 * Fetch all menu items organized by position
 * @param lang - Language code (en or ar)
 * @returns Promise with organized menu items
 */
export const fetchAllMenuItems = async (lang: string = 'en'): Promise<OrganizedMenuItems> => {
  const response = await fetchNavigationMenuItems(lang, 'all');
  return response.items as OrganizedMenuItems;
};